import { Box, CircularProgress, Dialog, Grid } from '@material-ui/core'
import { Button } from 'components/facets'
import { useCardData } from 'components/providers/CardDataProvider'
import { useUser } from 'components/providers/UserProvider'
import { ReactComponent as PtStockIcon } from 'img/ptStock.svg'
import { loadInventory } from 'lib/api'
import { useDialog } from 'lib/hooks'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import moment from 'moment'
import { Fragment, useEffect, useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import {
  AggregatedInventoryArticle,
  convertToAggregatedStock,
  FINISH_TYPE_LABELS,
  GAME_IDS,
  generateArticleKey,
  getSimilarArticle,
  getStockIndexedByMkmIdProduct,
  InventoryArticle,
  INVENTORY_STEPS,
  isSingle,
  PTInventoryArticle,
  PTInventoryProduct,
} from 'shared'
import { GameStockBox } from './GameStockBox'
import { SquareButton } from './SquareButton'
import { StockReviewFull } from './StockReviewFull'

interface CsvStockItem {
  cardmarketId: number
  name: string
  expansion: string
  language: string
  condition: string
  isFoil: boolean
  isFirstEd: boolean
  isSigned: boolean
  isReverseHolo: boolean
  quantity: number
  comment: string
}
interface StockReviewProps {
  incomingStock: InventoryArticle[]
  incomingAggregatedStock: AggregatedInventoryArticle[]
  // Set when the user reached this screen right after completing missing grades: skip the redundant recap
  // click and advance to source selection automatically once the inventory load + diff are ready.
  autoProceed?: boolean
  onAutoProceeded?: () => void
}

export const StockReview = ({
  incomingStock,
  incomingAggregatedStock,
  autoProceed = false,
  onAutoProceeded,
}: StockReviewProps): JSX.Element => {
  const { t } = useTranslation()
  const cardData = useCardData()
  const { user } = useUser()
  const history = useHistory()
  const [setLoadingDialogOpen, LoadingDialog] = useDialog()

  const {
    setStep,
    ptInventoryLoaded,
    setPtInventoryLoaded,
    currentPtInventory,
    setCurrentPtInventory,
    ptArticles,
    setPtArticles,
    setNumConflicts,
    setDiffCurrentInventory,
    setDiffIncomingInventory,
    paddedLeftData,
    setPaddedLeftData,
    paddedRightData,
    setPaddedRightData,
    setMatchingData,
    setDiffSelectedArticles,
  } = useInventoryStore()

  const [ptInventory, setPtInventory] = useState<PTInventoryProduct[]>([])
  const [fullListIdGame, setFullListIdGame] = useState(0)
  const [fullListType, setFullListType] = useState<'singles' | 'accessories'>('singles')
  const [showFullList, setShowFullList] = useState(false)
  const [commentPresent, setCommentsPresent] = useState(false)

  const isFirstImport = user?.ptInventory?.lastManualSyncWithCardmarketAt === null

  // forceGradeSync users can land here with no Cardmarket graded stock (their graded card isn't listed on
  // Cardmarket). Without this the empty-Cardmarket branch dead-ends them; instead let them proceed to
  // SourceSelection to reconcile the PowerTools-only graded cards (choose source of truth / push to CM).
  const forceGradeSyncWithoutCardmarketStock =
    !!user?.featureFlags?.forceGradeSync && incomingStock.length === 0 && ptArticles.length > 0

  const GAME_IDS_WITH_GENERIC = [0].concat(GAME_IDS)

  const defaultCounters: {
    [idGame: number]: {
      singles: number
      uniqueSingles: number
      sealed: number
      uniqueSealed: number
      totalUnique: number
    }
  } = {}
  Object.values(GAME_IDS_WITH_GENERIC).forEach((idGame) => {
    defaultCounters[idGame] = {
      singles: 0,
      uniqueSingles: 0,
      sealed: 0,
      uniqueSealed: 0,
      totalUnique: 0,
    }
  })
  const [counters, setCounters] = useState<{
    [idGame: number]: {
      singles: number
      uniqueSingles: number
      sealed: number
      uniqueSealed: number
      totalUnique: number
    }
  }>(defaultCounters)

  const [csvStock, setCsvStock] = useState<CsvStockItem[]>([])

  const filename = `import-${user?.cardmarketUsername}-${moment(Date.now()).format(
    'DD-MM-YYYY_HH-mm'
  )}.csv`

  const nextStep = () => {
    setStep(INVENTORY_STEPS.SOURCE_SELECTION.idx)
  }

  const handleStartImporting = async () => {
    // if (numConflicts === 0 && paddedLeftData.length === 0 && paddedRightData.length === 0) {
    //   setStep(INVENTORY_STEPS.PRICE_SELECTION.idx)
    // } else {
    nextStep()
    // }
  }

  const handleDownloadCsv = (done: (proceed: boolean | undefined) => void) => {
    if (window.Cypress) done(false)
    else done(true)
  }

  const handleShowFullList = (idGame: number, type: 'singles' | 'accessories') => {
    setFullListIdGame(idGame)
    setFullListType(type)
    setShowFullList(true)
  }

  useEffect(() => {
    const newCsvStock: CsvStockItem[] = []
    if (incomingStock.length !== 0) {
      const newCounters = { ...counters }
      // Cardmarket has one listing per price and comment: listings of the same product count once, as the
      // product they become on Powertools. The copies are the ones on Cardmarket, listing by listing
      const countedProducts = new Set<string>()
      incomingStock.forEach((article) => {
        const idGame = article.card.idGame
        if (idGame === undefined) return

        const productKey = generateArticleKey(article)
        const isNewProduct = !countedProducts.has(productKey)
        countedProducts.add(productKey)

        if (isSingle(article.card)) {
          newCounters[idGame].singles += article.quantity
          if (isNewProduct) {
            newCounters[idGame].uniqueSingles += 1
            newCounters[idGame].totalUnique += 1
          }
        } else {
          newCounters[idGame].sealed += article.quantity
          if (isNewProduct) {
            newCounters[idGame].uniqueSealed += 1
            newCounters[idGame].totalUnique += 1
          }
        }
        // add item to csvStock for later export
        const csvStockItem: CsvStockItem = {
          cardmarketId: article.card._id,
          name: article.card.name,
          expansion: article.card.expansion,
          language: article.languageName,
          condition: article.condition,
          isFoil: article.finishType === FINISH_TYPE_LABELS.FOIL,
          isFirstEd: article.isFirstEd,
          isSigned: article.isSigned,
          isReverseHolo: article.finishType === FINISH_TYPE_LABELS.REVERSE_HOLO,
          quantity: article.quantity,
          comment: article.comment,
        }
        newCsvStock.push(csvStockItem)

        if (article.comment && commentPresent === false) {
          setCommentsPresent(true)
        }
      })
      setCounters(newCounters)
    }
    setCsvStock(newCsvStock)
  }, [incomingStock])

  useEffect(() => {
    setCurrentPtInventory(ptInventory)
  }, [ptInventory])

  useEffect(() => {
    const newPtArticles: PTInventoryArticle[] = []
    ptInventory.forEach((ptInventoryProduct) => {
      ptInventoryProduct.articles.forEach((ptArticle) => {
        newPtArticles.push(ptArticle)
      })
    })
    // When the user was force-redirected here for a graded-stock mismatch (`forceGradeSync`), compare
    // ONLY graded cards: keep PowerTools articles that have a grading company. The cleanup is about
    // reconciling graded-card quantities (PT vs Cardmarket), so non-graded PT stock is deliberately
    // left out of the whole comparison — it never enters the diff and can never be touched (e.g. a
    // later "keep all from PowerTools" won't wipe non-graded Cardmarket stock, and vice versa).
    const ptArticlesToUse = user?.featureFlags?.forceGradeSync
      ? newPtArticles.filter((ptArticle) => !!ptArticle.ptProduct.gradingCompany)
      : newPtArticles
    setPtArticles(ptArticlesToUse)
  }, [currentPtInventory])

  const getPerfectMatches = (
    leftData: AggregatedInventoryArticle[],
    rightData: AggregatedInventoryArticle[]
  ) => {
    console.log(`getPerfectMatches`)

    const newLeftData = [...leftData]
    const newRightData = [...rightData]

    const newRightDataMap = new Map<string, number>()
    newRightData.forEach((item, index) => {
      const aggregatedArticleKey = generateArticleKey(item)
      newRightDataMap.set(`${aggregatedArticleKey}-${item.quantity}`, index)
    })

    // compute common data
    const newCommonData: AggregatedInventoryArticle[] = []
    newLeftData.forEach((leftItem) => {
      const aggregatedArticleKey = generateArticleKey(leftItem)
      const key = `${aggregatedArticleKey}-${leftItem.quantity}`
      const rightItemIndex = newRightDataMap.get(key)
      if (rightItemIndex !== undefined) {
        newCommonData.push(leftItem)
        leftItem.isSelectedForConflictResolution = true
        leftItem.isProcessed = true
        newRightData[rightItemIndex].isProcessed = true
      }
    })
    setMatchingData(newCommonData)

    return { newLeftData, newRightData, newCommonData }
  }

  const getPartialMatches = (
    leftData: AggregatedInventoryArticle[],
    rightData: AggregatedInventoryArticle[],
    paddedLeftData: (AggregatedInventoryArticle | undefined)[],
    paddedRightData: (AggregatedInventoryArticle | undefined)[]
  ) => {
    console.log(`getPartialMatches`)
    const newLeftData = [...leftData]
    const indexedRightData = getStockIndexedByMkmIdProduct(rightData)
    let newNumConflicts = 0

    newLeftData.forEach((leftItem) => {
      const rightDataItems = indexedRightData.get(leftItem.idProduct)
      if (rightDataItems) {
        const { similarArticle, distanceFields } = getSimilarArticle(leftItem, rightDataItems)
        // if there's a similar article, we don't add it to the extra data but
        // we add the other articles from the rightDataItems to the extra data
        if (!similarArticle) {
          return
        }

        leftItem.hasConflict = true
        similarArticle.hasConflict = true
        leftItem.conflictFields = distanceFields
        similarArticle.conflictFields = distanceFields
        paddedLeftData.push(leftItem)
        paddedRightData.push(similarArticle)
        newNumConflicts++

        leftItem.isProcessed = true
        similarArticle.isProcessed = true
      }
    })
    setNumConflicts(newNumConflicts)

    const newRightData = []
    for (const rightItem of indexedRightData.values()) {
      newRightData.push(...rightItem)
    }

    return {
      noMatchesLeftData: newLeftData,
      noMatchesRightData: newRightData,
      partialPaddedLeftData: paddedLeftData,
      partialPaddedRightData: paddedRightData,
    }
  }

  const getNoMatches = (
    leftData: AggregatedInventoryArticle[],
    rightData: AggregatedInventoryArticle[],
    paddedLeftData: (AggregatedInventoryArticle | undefined)[],
    paddedRightData: (AggregatedInventoryArticle | undefined)[]
  ) => {
    console.log(`getNoMatches`)
    let leftIndex = 0
    let rightIndex = 0

    while (leftIndex < leftData.length && rightIndex < rightData.length) {
      const leftItem = leftData[leftIndex]
      const rightItem = rightData[rightIndex]

      // when considering reserved quantities, it's possible that the item coming from Powertools
      // has a final quantity of 0. In that case, we skip it
      if (leftItem.quantity === 0) {
        leftIndex++
        continue
      }

      if (leftItem.sku < rightItem.sku) {
        leftItem.isSelectedForConflictResolution = true
        paddedLeftData.push(leftItem)
        paddedRightData.push(undefined)
        leftIndex++
      } else {
        paddedLeftData.push(undefined)
        rightItem.isSelectedForConflictResolution = true
        paddedRightData.push(rightItem)
        rightIndex++
      }
    }
    while (leftIndex < leftData.length) {
      const leftItem = leftData[leftIndex]
      // when considering reserved quantities, it's possible that the item coming from Powertools
      // has a final quantity of 0. In that case, we skip it
      if (leftItem.quantity === 0) {
        leftIndex++
        continue
      }

      leftItem.isSelectedForConflictResolution = true
      paddedLeftData.push(leftItem)
      paddedRightData.push(undefined)
      leftIndex++
    }
    while (rightIndex < rightData.length) {
      paddedLeftData.push(undefined)
      const rightItem = rightData[rightIndex]
      rightItem.isSelectedForConflictResolution = true
      paddedRightData.push(rightItem)
      rightIndex++
    }

    return { finalPaddedLeftData: paddedLeftData, finalPaddedRightData: paddedRightData }
  }

  const computePaddedDiffs = (
    currentAggregatedStock: AggregatedInventoryArticle[],
    incomingAggregatedStock: AggregatedInventoryArticle[]
  ) => {
    console.log(`computePaddedDiffs`)
    const start = Date.now()

    const newDiffSelectedArticles: AggregatedInventoryArticle[] = []
    let newPaddedLeftData: (AggregatedInventoryArticle | undefined)[] = []
    let newPaddedRightData: (AggregatedInventoryArticle | undefined)[] = []

    // compute common data
    const { newLeftData, newRightData, newCommonData } = getPerfectMatches(
      currentAggregatedStock,
      incomingAggregatedStock
    )

    let unprocessedLeftData = newLeftData.filter((item) => !item.isProcessed)
    let unprocessedRightData = newRightData.filter((item) => !item.isProcessed)

    // compute partial matches
    const { noMatchesLeftData, noMatchesRightData, partialPaddedRightData, partialPaddedLeftData } =
      getPartialMatches(
        unprocessedLeftData,
        unprocessedRightData,
        newPaddedLeftData,
        newPaddedRightData
      )
    newPaddedLeftData = partialPaddedLeftData
    newPaddedRightData = partialPaddedRightData

    // compute no matches
    unprocessedLeftData = noMatchesLeftData.filter((item) => !item.isProcessed)
    unprocessedRightData = noMatchesRightData.filter((item) => !item.isProcessed)

    const { finalPaddedLeftData, finalPaddedRightData } = getNoMatches(
      unprocessedLeftData,
      unprocessedRightData,
      newPaddedLeftData,
      newPaddedRightData
    )
    newPaddedLeftData = finalPaddedLeftData
    newPaddedRightData = finalPaddedRightData

    setPaddedLeftData(finalPaddedLeftData)
    setPaddedRightData(finalPaddedRightData)

    newCommonData.forEach((item) => {
      if (item && item.isSelectedForConflictResolution) {
        newDiffSelectedArticles.push(item)
      }
    })
    finalPaddedLeftData.forEach((item) => {
      if (item && item.isSelectedForConflictResolution) {
        newDiffSelectedArticles.push(item)
      }
    })
    finalPaddedRightData.forEach((item) => {
      if (item && item.isSelectedForConflictResolution) {
        newDiffSelectedArticles.push(item)
      }
    })
    setDiffSelectedArticles(newDiffSelectedArticles)

    const end = Date.now()
    console.log(`computePaddedDiffs took ${(end - start) / 1000} seconds`)

    return partialPaddedLeftData.length
  }

  const sortAggregatedArticles = (aggregatedArticles: AggregatedInventoryArticle[]) => {
    const sortedAggregatedArticles = aggregatedArticles.sort((a, b) => {
      if (a.idProduct === b.idProduct) {
        return 0
      } else if (a.idProduct < b.idProduct) {
        return -1
      } else {
        return 1
      }
    })
    return sortedAggregatedArticles
  }

  const computeDiffLists = () => {
    console.log(`computeDiffLists`)
    // compute differences between current inventory and incoming inventory
    const currentAggregatedStock = convertToAggregatedStock(cardData, ptArticles)
    const numberOfConflicts = computePaddedDiffs(currentAggregatedStock, incomingAggregatedStock)

    if (numberOfConflicts === 0 && paddedLeftData.length === 0 && paddedRightData.length === 0) {
      // setStep(INVENTORY_STEPS.PRICE_SELECTION.idx)
      console.log(`nothing to do in diff viewer`)
    } else {
      const sortedDiffCurrentInventory = sortAggregatedArticles(currentAggregatedStock)
      const sortedDiffIncomingInventory = sortAggregatedArticles(incomingAggregatedStock)

      setDiffCurrentInventory(sortedDiffCurrentInventory)
      setDiffIncomingInventory(sortedDiffIncomingInventory)
    }
    setLoadingDialogOpen(false)

    // Coming straight from grade completion: the recap here would be a second identical screen, so advance
    // to source selection as soon as the diff is ready (same effect as clicking "start import"). Reset the
    // flag first so a later diff recompute can't re-trigger it.
    if (autoProceed) {
      onAutoProceeded?.()
      nextStep()
    }
  }

  useEffect(() => {
    if (ptInventoryLoaded) {
      console.log(`currentPtInventory.length ${currentPtInventory.length}`)
      computeDiffLists()
    }
  }, [ptArticles, ptInventoryLoaded])

  useEffect(() => {
    const handleFetchPTInventory = async () => {
      console.log(`handleFetchPTInventory`)
      setLoadingDialogOpen(true)
      const start = Date.now()
      const ptInventory = await loadInventory()
      setPtInventory(ptInventory)
      const end = Date.now()
      console.log(`fetch inventory took ${(end - start) / 1000} seconds`)
      setPtInventoryLoaded(true)
    }

    handleFetchPTInventory()
  }, [])

  return (
    <>
      {incomingStock.length > 0 ? (
        <>
          <Box>
            <Box className="title" marginBottom={5}>
              <div className="titleFont">{t('ptInventory.stockReview.title')}</div>
            </Box>

            <Grid container spacing={1}>
              {GAME_IDS_WITH_GENERIC.map((gameId) => {
                return (
                  <Fragment key={`gameBox-${gameId}`}>
                    {counters[gameId].totalUnique > 0 && (
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <GameStockBox
                          idGame={gameId}
                          counters={counters[gameId]}
                          handleShowFullList={handleShowFullList}
                        />
                      </Grid>
                    )}
                  </Fragment>
                )
              })}
            </Grid>
            <Box marginY={5} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
                <Box className="inventoryButtonContainer">
                  <Button
                    data-testid="start-import-btn"
                    variant="contained"
                    className="inventoryButton"
                    onClick={handleStartImporting}
                  >
                    {isFirstImport
                      ? t('ptInventory.stockReview.startImport.firstTimeImport')
                      : t('ptInventory.stockReview.startImport.notFirstTimeImport')}
                  </Button>
                </Box>
              </Grid>
              <Grid data-testid="download-csv-btn" item xs={12} sm={12} md={6} lg={6} xl={3}>
                <Box>
                  <CSVLink
                    data={csvStock}
                    asyncOnClick={true}
                    onClick={(_event, done) => {
                      handleDownloadCsv(done)
                    }}
                    enclosingCharacter={`"`}
                    filename={filename}
                    style={{ textDecoration: 'none' }}
                  >
                    <Button className="inventoryEmptyButton" variant="outlined">
                      {t('ptInventory.stockReview.downloadCsv')}
                    </Button>
                  </CSVLink>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Dialog maxWidth="lg" open={showFullList} onClose={() => setShowFullList(false)}>
            <div className="p-5">
              <h3>{t('ptInventory.stockReview.completeReview')}</h3>
              <StockReviewFull
                incomingStock={incomingStock}
                idGame={fullListIdGame}
                type={fullListType}
                handleClose={() => setShowFullList(false)}
              />
            </div>
          </Dialog>
        </>
      ) : forceGradeSyncWithoutCardmarketStock ? (
        <>
          <Box marginBottom={3}>
            <h3>{t('ptInventory.stockReview.forceGradeSyncNoCardmarketStock')}</h3>
          </Box>
          <Box className="inventoryButtonContainer">
            <Button
              data-testid="start-import-btn"
              variant="contained"
              className="inventoryButton"
              onClick={handleStartImporting}
            >
              {t('ptInventory.stockReview.forceGradeSyncProceed')}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <h3>{t('ptInventory.stockReview.emptyCardmarketStock')}</h3>
          <Grid container spacing={2}>
            <Grid data-testid="go-to-stock-btn" item xs={1}>
              <SquareButton
                image={<PtStockIcon />}
                text={t('ptInventory.welcome.goToStock')}
                type="secondary"
                handleClick={() => history.push('/stock-pricing')}
              />
            </Grid>
          </Grid>
        </>
      )}

      <LoadingDialog
        title="Loading..."
        content={
          <Box marginTop={3} height="8rem" width="fit-content" margin="0 auto">
            <CircularProgress size={100} />
          </Box>
        }
      />
    </>
  )
}
